// Key numbers for the whole church, shown to administrators and auditors on /admin: the quarter's
// headline figures, church income by fund, each department's quarter so far, and what just happened.
// Each block loads and fails on its own; every query runs under RLS through src/data.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { Activity } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { EmptyState } from '@/components/ui/EmptyState'
import { MoneyText } from '@/components/ui/MoneyText'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { StatTile } from '@/components/ui/StatTile'
import { listWaitingBudgets } from '@/data/budgets'
import { listWaitingExpenses } from '@/data/expenses'
import { listHistory } from '@/data/history'
import { fundBalances, quarterIncome } from '@/data/income'
import { listPositions } from '@/data/remittances'
import type { Department } from '@/data/departments'
import { currentQuarterId } from '@/lib/periods'
import { CardError, CardLoading, InsightCard } from './Greeting'
import { AmountBarsChart, CollectedSpentChart } from './LazyCharts'
import { heldNotSent, quarterLabel, relativeTime } from './insights'

const linkClass = 'text-label font-medium text-brand hover:underline'

/** The four numbers an administrator looks for first. Collected and Spent are the departments' totals, as on the Church summary. */
export function KpiRow() {
  const quarter = currentQuarterId()
  const positions = useQuery({ queryKey: ['position', 'church', quarter], queryFn: () => listPositions({ quarterIds: [quarter] }) })
  const balances = useQuery({ queryKey: ['income', 'balances'], queryFn: fundBalances })
  const waitingBudgets = useQuery({ queryKey: ['waiting-budgets'], queryFn: listWaitingBudgets })
  const waitingExpenses = useQuery({ queryKey: ['waiting-expenses'], queryFn: listWaitingExpenses })

  const rows = positions.data
  const collected = rows ? rows.reduce((s, p) => s + p.collectedNgwee, 0) : undefined
  const spent = rows ? rows.reduce((s, p) => s + p.spentLocalNgwee + p.spentCentralNgwee, 0) : undefined
  const held = balances.data ? heldNotSent(balances.data) : undefined
  const waiting = waitingBudgets.data && waitingExpenses.data ? waitingBudgets.data.length + waitingExpenses.data.length : undefined

  const money = (v: number | undefined, failed: boolean) => (failed ? '—' : v === undefined ? undefined : <MoneyText ngwee={v} />)
  const failedHint = 'Could not load. Open to see more.'

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="This quarter at a glance">
      <Link to="/admin/reports" className="block rounded-card">
        <StatTile
          interactive
          label="Collected this quarter"
          value={money(collected, positions.isError)}
          detail={positions.isError ? failedHint : `Departments, ${quarterLabel(quarter)}`}
        />
      </Link>
      <Link to="/admin/reports" className="block rounded-card">
        <StatTile
          interactive
          label="Spent this quarter"
          value={money(spent, positions.isError)}
          detail={positions.isError ? failedHint : `Departments, ${quarterLabel(quarter)}`}
        />
      </Link>
      <Link to="/admin/income" className="block rounded-card">
        <StatTile
          interactive
          label="Held to pass on"
          value={money(held, balances.isError)}
          detail={balances.isError ? failedHint : 'Tithe and other money owed to the Conference'}
        />
      </Link>
      <Link to="/admin/approvals" className="block rounded-card">
        <StatTile
          interactive
          label="Waiting for approval"
          value={waitingBudgets.isError || waitingExpenses.isError ? '—' : waiting === undefined ? undefined : String(waiting)}
          tone={waiting ? 'warning' : 'default'}
          detail={waiting === 0 ? 'Nothing waiting for a decision' : 'Budgets and expenses to decide'}
        />
      </Link>
    </div>
  )
}

/** Church income by fund, and each department's quarter side by side. */
export function ChurchCharts({ departments }: { departments: Department[] | undefined }) {
  const quarter = currentQuarterId()
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <IncomeCard quarter={quarter} />
      <DepartmentsCard quarter={quarter} departments={departments} />
    </div>
  )
}

function IncomeCard({ quarter }: { quarter: string }) {
  const income = useQuery({ queryKey: ['income', 'quarter', quarter], queryFn: () => quarterIncome(quarter) })
  const rows = (income.data ?? []).filter((r) => r.totalNgwee !== 0)
  const totalNgwee = rows.reduce((s, r) => s + r.totalNgwee, 0)

  let body: ReactNode
  if (income.isPending) body = <CardLoading />
  else if (income.isError) body = <CardError error={income.error} retry={() => void income.refetch()} />
  else if (rows.length === 0) body = <p className="text-body text-muted">Nothing has been counted yet this quarter.</p>
  else
    body = (
      <>
        <p className="text-title font-semibold tabular-nums text-ink">
          <MoneyText ngwee={totalNgwee} />
        </p>
        <AmountBarsChart data={rows.map((r) => ({ label: r.fundName, ngwee: r.totalNgwee }))} name="Counted" />
      </>
    )

  return (
    <InsightCard
      title={`Church income, ${quarterLabel(quarter)}`}
      action={<Link to="/admin/income" className={linkClass}>Income</Link>}
    >
      {body}
    </InsightCard>
  )
}

function DepartmentsCard({ quarter, departments }: { quarter: string; departments: Department[] | undefined }) {
  const positions = useQuery({ queryKey: ['position', 'church', quarter], queryFn: () => listPositions({ quarterIds: [quarter] }) })
  const nameOf = (id: string) => departments?.find((d) => d.id === id)?.name ?? 'Department'
  const rows = (positions.data ?? [])
    .map((p) => ({
      id: p.departmentId,
      label: nameOf(p.departmentId),
      collectedNgwee: p.collectedNgwee,
      spentNgwee: p.spentLocalNgwee + p.spentCentralNgwee,
      cashOnHandNgwee: p.cashOnHandNgwee,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  let body: ReactNode
  if (positions.isPending) body = <CardLoading />
  else if (positions.isError) body = <CardError error={positions.error} retry={() => void positions.refetch()} />
  else if (rows.length === 0) body = <p className="text-body text-muted">No departments yet.</p>
  else
    body = (
      <>
        <CollectedSpentChart data={rows} height={220} />
        <div className="overflow-x-auto">
          <table className="w-full text-body">
            <caption className="sr-only">Each department this quarter</caption>
            <thead>
              <tr className="text-left text-label text-muted">
                <th scope="col" className="py-1 font-medium">Department</th>
                <th scope="col" className="py-1 text-right font-medium">Collected</th>
                <th scope="col" className="py-1 text-right font-medium">Spent</th>
                <th scope="col" className="py-1 text-right font-medium">Cash held</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="py-1.5 pr-2">
                    <Link to="/d/$deptId" params={{ deptId: r.id }} className="font-medium text-brand hover:underline">
                      {r.label}
                    </Link>
                  </td>
                  <td className="py-1.5 text-right"><MoneyText ngwee={r.collectedNgwee} /></td>
                  <td className="py-1.5 text-right"><MoneyText ngwee={r.spentNgwee} /></td>
                  <td className="py-1.5 text-right"><MoneyText ngwee={r.cashOnHandNgwee} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )

  return (
    <InsightCard
      title={`Departments, ${quarterLabel(quarter)}`}
      action={<Link to="/admin/reports" className={linkClass}>Summary</Link>}
    >
      {body}
    </InsightCard>
  )
}

const RECENT = 8

/** The latest events, read from the same audit trail as the History screen (so the same row-level security). */
export function RecentActivity({ className }: { className?: string }) {
  const history = useQuery({ queryKey: ['home', 'recent-activity'], queryFn: () => listHistory({}), staleTime: 30_000 })
  const entries = history.data?.entries.slice(0, RECENT) ?? []

  let body: ReactNode
  if (history.isPending) body = <SkeletonRows rows={5} />
  else if (history.isError) body = <CardError error={history.error} retry={() => void history.refetch()} />
  else if (entries.length === 0)
    body = <EmptyState icon={Activity} title="Nothing has happened yet" description="Money counted, approved and confirmed will show up here." />
  else
    body = (
      <ul className="divide-y divide-line" aria-label="Recent activity">
        {entries.map((e) => (
          <li key={e.id} className="flex items-start gap-3 py-2.5">
            <Avatar name={e.who} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="text-body text-ink">
                <span className="font-medium">{e.who}</span> {e.verb} {e.op === 'INSERT' ? (/^[aeiou]/i.test(e.kind) ? 'an' : 'a') : 'the'} {e.kind}
              </p>
              {e.label && <p className="truncate text-label text-muted">{e.label}</p>}
            </div>
            <time dateTime={e.at} className="shrink-0 text-label text-muted">
              {relativeTime(e.at)}
            </time>
          </li>
        ))}
      </ul>
    )

  return (
    <InsightCard title="Recent activity" className={className} action={<Link to="/admin/history" className={linkClass}>See all</Link>}>
      {body}
    </InsightCard>
  )
}
